import { useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  listDeployments, listDiscoveryPackagingJobs,
  type Deployment, type ManagementState, type DiscoveryPackagingJob,
} from '../services/api'
import {
  ArrowLongRightIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloudIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  QueueListIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline'
import {
  PageHeader, Tabs, StatCard, TableCard, SearchInput, Select, Badge, EmptyState, TableSkeleton,
  SyncStatus, type BadgeTone, type IconComponent,
} from '../components/ui'
import { useIntuneQuery, intuneKeys } from '../hooks/useIntuneQuery'
import { dateTime, relTime } from '../utils/date'
import AddToCatalogModal from '../components/AddToCatalogModal'
import ConvertToManagedModal from '../components/ConvertToManagedModal'
import ConversionsMonitor from '../components/ConversionsMonitor'

type ManagementFilter = 'all' | ManagementState

/**
 * Convert to Managed — uninstall the unmanaged copy on every device, then deploy the managed one —
 * is hidden from this page. Packaging an app and replacing it on endpoints are separate decisions,
 * and Discovery only offers the first. The flow, its modal and its API are kept intact; flip this
 * to bring the bulk action back.
 */
const SHOW_CONVERT_TO_MANAGED = false

/** Rows per page, as the mock's list pages (its discovery page.tsx:26). */
const PAGE = 14

const statusTone: Record<Deployment['status'], BadgeTone> = {
  active: 'green',
  pending: 'amber',
  detected: 'slate',
  failed: 'red',
  unknown: 'slate',
  removed: 'slate',
}

const targetTypeLabels: Record<string, string> = {
  intune: 'Microsoft Intune',
  sccm: 'SCCM',
  appstore_client: 'Splashtop Agentic Packr client',
  endpoint: 'Endpoint inventory',
}

// Derive management state with a fallback for older API responses.
const stateOf = (d: Deployment): ManagementState =>
  d.management_state ?? (d.in_catalog ? 'sap' : 'intune')

const stateBadges: Record<ManagementState, { label: string; tone: BadgeTone }> = {
  sap: { label: 'Managed', tone: 'green' },
  intune: { label: 'Intune', tone: 'blue' },
  endpoint: { label: 'Unmanaged', tone: 'slate' },
}

// A managed app is already in the catalog, so neither bulk action can do anything with it.
const isSelectable = (d: Deployment): boolean => stateOf(d) !== 'sap'

export default function Discovery() {
  const [filter, setFilter] = useState<ManagementFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set())
  const [showModal, setShowModal] = useState(false)
  const [showConvertModal, setShowConvertModal] = useState(false)
  // A single row's Package action packages just that app, without disturbing the selection.
  const [packageOne, setPackageOne] = useState<Deployment | null>(null)
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'apps' | 'conversions'>('apps')
  const [page, setPage] = useState(0)
  const queryClient = useQueryClient()

  // Lightweight poll just for the tab badge (packaging records).
  const { data: convData } = useQuery({
    queryKey: ['discovery-packaging-jobs'],
    queryFn: listDiscoveryPackagingJobs,
    refetchInterval: 15000,
  })
  const totalConversions = (convData?.jobs || []).length

  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useIntuneQuery({
    queryKey: intuneKeys.deployments(),
    queryFn: listDeployments,
    refetchInterval: 30000,
  })

  const deployments = useMemo(() => data?.deployments || [], [data])

  const stats = useMemo(() => ({
    total: deployments.length,
    sap: deployments.filter((d: Deployment) => stateOf(d) === 'sap').length,
    intune: deployments.filter((d: Deployment) => stateOf(d) === 'intune').length,
    endpoint: deployments.filter((d: Deployment) => stateOf(d) === 'endpoint').length,
  }), [deployments])

  const filteredApps = useMemo(() => deployments.filter((deployment: Deployment) => {
    if (filter !== 'all' && stateOf(deployment) !== filter) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return (
        deployment.app_name.toLowerCase().includes(q) ||
        (deployment.publisher || '').toLowerCase().includes(q) ||
        (deployment.version || '').toLowerCase().includes(q)
      )
    }
    return true
  }), [deployments, filter, searchQuery])

  const pageCount = Math.max(1, Math.ceil(filteredApps.length / PAGE))
  // Clamp rather than trust `page`: a background refetch can shrink the list under us, and a page
  // index past the end would render an empty table over a non-empty result.
  const safePage = Math.min(page, pageCount - 1)
  const pageRows = filteredApps.slice(safePage * PAGE, safePage * PAGE + PAGE)
  const resetPage = () => setPage(0)

  const toggleSelectApp = (id: string) => {
    const newSelected = new Set(selectedApps)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedApps(newSelected)
  }

  // Select-all covers the visible page only — the checkbox sits in that page's header row — and
  // skips managed apps, whose own checkbox is disabled.
  const pageSelectable = pageRows.filter(isSelectable)
  const pageAllSelected = pageSelectable.length > 0 && pageSelectable.every((a) => selectedApps.has(a.id))
  const toggleSelectAll = () => {
    const next = new Set(selectedApps)
    if (pageAllSelected) pageSelectable.forEach((a) => next.delete(a.id))
    else pageSelectable.forEach((a) => next.add(a.id))
    setSelectedApps(next)
  }

  const handleAddToCatalog = () => {
    // Only include unmanaged apps
    const unmanagedSelected = Array.from(selectedApps).filter(id => {
      const dep = deployments.find((d: Deployment) => d.id === id)
      return dep && !dep.in_catalog
    })

    if (unmanagedSelected.length === 0) {
      setToastMessage('All selected apps are already in catalog')
      setTimeout(() => setToastMessage(null), 3000)
      return
    }

    setShowModal(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    // A row's own Package action never touched the selection, so closing it must not clear one.
    if (!packageOne) setSelectedApps(new Set())
    setPackageOne(null)
    queryClient.invalidateQueries({ queryKey: intuneKeys.deployments() })
    queryClient.invalidateQueries({ queryKey: ['discovery-packaging-jobs'] })
  }

  const handleConvertToManaged = () => {
    const unmanagedSelected = Array.from(selectedApps).filter(id => {
      const dep = deployments.find((d: Deployment) => d.id === id)
      return dep && stateOf(dep) === 'endpoint'
    })
    if (unmanagedSelected.length === 0) {
      setToastMessage('Convert to Managed only applies to Unmanaged (endpoint) apps')
      setTimeout(() => setToastMessage(null), 3000)
      return
    }
    setShowConvertModal(true)
  }

  const handleConvertModalClose = () => {
    setShowConvertModal(false)
    setSelectedApps(new Set())
  }

  // Apps to package: the row whose Package button was pressed, else the selected rows that are
  // not already in the catalog.
  const toPackageItem = (d: Deployment) => ({
    id: d.id,
    app_name: d.app_name,
    publisher: d.publisher,
    version: d.version,
    arch: d.architecture ?? null,
    management_state: stateOf(d),
    detected_app_id: d.detected_app_id ?? null,
  })
  const appsToPackage = packageOne
    ? [toPackageItem(packageOne)]
    : Array.from(selectedApps)
        .map(id => deployments.find((d: Deployment) => d.id === id))
        .filter((d): d is Deployment => !!d && !d.in_catalog)
        .map(toPackageItem)

  // Convert-to-Managed input shape (discoveryMatch expects `name`).
  const toConvertItem = (d: Deployment) => ({
    id: d.id,
    name: d.app_name,
    version: d.version,
    arch: d.architecture ?? null,
    publisher: d.publisher,
    management_state: stateOf(d),
    detected_app_id: d.detected_app_id ?? null,
  })
  const selectedUnmanagedForConvert = Array.from(selectedApps)
    .map(id => deployments.find((d: Deployment) => d.id === id))
    .filter((d): d is Deployment => !!d && stateOf(d) === 'endpoint')
    .map(toConvertItem)

  // Only a failure with nothing cached takes the page; a failed refresh over existing rows keeps
  // them on screen and reports itself in a banner instead.
  if (error && !data) {
    return (
      <div className="p-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6">
          <h2 className="text-red-700 dark:text-red-400 font-medium mb-2">Failed to load discovery data</h2>
          <p className="text-theme-text-secondary text-sm">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  // Whether Intune is connected is UNKNOWN until the first read lands, and unknown is not
  // unconfigured: `intune_configured` says the tenant has stored Intune credentials, and reading
  // it off an undefined response tells a configured tenant to go and configure Intune for the
  // several seconds the Graph call takes.
  const intuneKnown = data !== undefined
  const intuneConfigured = data?.intune_configured
  const intuneError = data?.intune_error
  const detectedAppsError = data?.detected_apps_error

  return (
    <div className="p-8">
      <PageHeader
        title="Discovery"
        subtitle="Apps managed by Agentic Packr, managed by Intune, and detected on endpoints"
        actions={
          <div className="flex items-center gap-3">
          <SyncStatus
            syncing={isFetching}
            updatedAt={dataUpdatedAt}
            label="Syncing with Microsoft Intune…"
          />
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 rounded-lg border border-theme-border-primary px-3 py-2 text-sm text-theme-text-primary hover:bg-theme-tertiary disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          </div>
        }
      />

      <div className="mb-5">
        <Tabs
          tabs={[
            { key: 'apps', label: 'Discovered Apps' },
            { key: 'conversions', label: 'Conversions', count: totalConversions || undefined },
          ]}
          active={activeTab}
          onChange={(k) => { setActiveTab(k as 'apps' | 'conversions'); resetPage() }}
        />
      </div>

      {error && (
        <Banner tone="red" icon={ExclamationTriangleIcon} title="Could not refresh from Intune">
          Showing the apps from the last successful read. {(error as Error).message}
        </Banner>
      )}

      {/* Intune connection state — above the tab content, as the mock has it: which tab you are on
          does not change whether the tenant is connected.

          NOT SHOWN WHILE THE ANSWER IS UNKNOWN, and unknown covers two states: the first read has
          not landed, and the read that landed could not tell — the backend reports
          `intune_configured: false` when the configuration lookup itself threw, and "go and
          configure Intune" sends a configured tenant nowhere. */}
      {intuneKnown && !intuneConfigured && !intuneError && (
        <Banner tone="amber" icon={ExclamationTriangleIcon} title="Intune not configured">
          Configure Microsoft Intune integration in{' '}
          <Link to="/settings" className="text-theme-accent-blue hover:underline">Settings</Link>{' '}
          to discover applications from Intune.
        </Banner>
      )}

      {intuneError && (
        <Banner tone="red" icon={ExclamationTriangleIcon} title="Failed to connect to Intune">
          {intuneError}
        </Banner>
      )}

      {intuneConfigured && !intuneError && (
        <Banner tone="green" icon={CheckCircleIcon} title="Connected to Microsoft Intune">
          Showing apps managed by Agentic Packr, managed by Intune, and detected on endpoints.
        </Banner>
      )}

      {intuneConfigured && detectedAppsError && (
        <Banner tone="blue" icon={ExclamationTriangleIcon} title="Endpoint discovery unavailable">
          Managed apps are shown, but apps installed on endpoints could not be read. The Intune
          app registration likely needs the <code>DeviceManagementManagedDevices.Read.All</code> Graph
          permission (admin consent). Details: {detectedAppsError}
        </Banner>
      )}

      {activeTab === 'conversions' && (
        <>
          <ConversionsView />
          {/* The uninstall-then-redeploy jobs and their controls, shown only where that flow is. */}
          {SHOW_CONVERT_TO_MANAGED && (
            <div className="mt-8">
              <h2 className="mb-3 text-sm font-semibold text-theme-text-primary">Convert to Managed jobs</h2>
              <ConversionsMonitor />
            </div>
          )}
        </>
      )}

      {activeTab === 'apps' && (
      <>
      {/* Stats Overview */}
      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total Discovered" value={stats.total} icon={QueueListIcon} tone="blue" />
        <StatCard label="Managed" value={stats.sap} icon={ShieldCheckIcon} tone="green" />
        <StatCard label="Intune" value={stats.intune} icon={CloudIcon} tone="amber" />
        <StatCard label="Unmanaged" value={stats.endpoint} icon={ExclamationTriangleIcon} tone="red" />
      </div>

      {/* Toast */}
      {toastMessage && (
        <div className="mb-4 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3">
          <p className="text-sm text-amber-600 dark:text-amber-300">{toastMessage}</p>
        </div>
      )}

      <TableCard
        toolbar={
          <>
            <SearchInput
              value={searchQuery}
              onChange={(v) => { setSearchQuery(v); resetPage() }}
              placeholder="Search apps..."
              className="w-full max-w-sm"
            />
            {/* Width on a wrapper, not the control: `Select` is `w-full` and two width utilities on
                one element resolve by stylesheet order, not by the order they are written. */}
            <div className="w-52">
            <Select
              value={filter}
              onChange={(v) => { setFilter(v as ManagementFilter); setSelectedApps(new Set()); resetPage() }}
              options={[
                { value: 'all', label: `All apps (${stats.total})` },
                { value: 'sap', label: `Managed (${stats.sap})` },
                { value: 'intune', label: `Intune (${stats.intune})` },
                { value: 'endpoint', label: `Unmanaged (${stats.endpoint})` },
              ]}
            />
            </div>
            {selectedApps.size > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-sm text-theme-text-tertiary">{selectedApps.size} selected</span>
                <button
                  onClick={handleAddToCatalog}
                  className="rounded bg-green-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-green-700"
                >
                  Add to Catalog
                </button>
                {SHOW_CONVERT_TO_MANAGED && (
                  <button
                    onClick={handleConvertToManaged}
                    className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-blue-700"
                  >
                    Convert to Managed
                  </button>
                )}
              </div>
            )}
          </>
        }
        footer={
          <>
            <span>{filteredApps.length} app{filteredApps.length !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-2">
              <span>Page {safePage + 1} of {pageCount}</span>
              <button
                aria-label="Previous page"
                className="rounded-md p-1 text-theme-text-secondary hover:bg-theme-tertiary disabled:opacity-40"
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button
                aria-label="Next page"
                className="rounded-md p-1 text-theme-text-secondary hover:bg-theme-tertiary disabled:opacity-40"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </>
        }
      >
        <thead>
          <tr className="border-b border-theme-border-primary">
            <th className="th w-10">
              <input
                type="checkbox"
                aria-label="Select all apps on this page"
                checked={pageAllSelected}
                disabled={pageSelectable.length === 0}
                onChange={toggleSelectAll}
                className="rounded border-theme-border-primary text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
              />
            </th>
            <th className="th">App</th>
            <th className="th">Version</th>
            <th className="th">Status</th>
            <th className="th">Source</th>
            <th className="th">Management</th>
            <th className="th">Last Updated</th>
            <th className="th text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <TableSkeleton cols={8} />
          ) : pageRows.length === 0 ? (
            <tr>
              <td colSpan={8}>
                {deployments.length === 0 ? (
                  <EmptyState
                    icon={<QueueListIcon className="h-7 w-7" />}
                    title="No apps discovered"
                    hint="Configure Intune integration to discover apps in your environment."
                  />
                ) : (
                  <EmptyState
                    icon={<QueueListIcon className="h-7 w-7" />}
                    title="No apps match"
                    hint="Try a different filter or search."
                  />
                )}
              </td>
            </tr>
          ) : (
            pageRows.map((deployment: Deployment) => {
              const st = stateOf(deployment)
              const badge = stateBadges[st]
              return (
                <tr key={deployment.id} className="row-hover border-b border-theme-border-primary last:border-0">
                  <td className="td">
                    <input
                      type="checkbox"
                      aria-label={`Select ${deployment.app_name}`}
                      checked={selectedApps.has(deployment.id)}
                      disabled={st === 'sap'}
                      title={st === 'sap' ? 'Already a managed app' : undefined}
                      onChange={() => toggleSelectApp(deployment.id)}
                      className="rounded border-theme-border-primary text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </td>
                  <td className="td">
                    {/* Cap the cell's content, not the cell: a table in auto layout sizes a column
                        to its widest no-wrap content, so `truncate` alone lets an X.500 publisher
                        DN ("CN=Adobe Inc., OU=…") stretch this column and crush the rest. The cap
                        goes on a plain div because max-width on a <td> is ignored in auto layout. */}
                    <div className="flex max-w-[22rem] items-center gap-3">
                      {deployment.icon_url ? (
                        <img
                          src={deployment.icon_url}
                          alt={deployment.app_name || 'App icon'}
                          className="h-[30px] w-[30px] shrink-0 rounded-lg bg-theme-tertiary object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                            target.nextElementSibling?.classList.remove('hidden')
                          }}
                        />
                      ) : null}
                      <div className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-theme-tertiary ${deployment.icon_url ? 'hidden' : ''}`}>
                        <span className="font-medium text-theme-text-primary">{deployment.app_name?.charAt(0) || '?'}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-theme-text-primary" title={deployment.app_name || undefined}>
                          {deployment.app_name || 'Unknown App'}
                        </div>
                        <div className="truncate text-[12px] text-theme-text-tertiary" title={deployment.publisher || undefined}>
                          {deployment.publisher || 'Unknown Publisher'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="td tabular-nums text-theme-text-secondary">{deployment.version || 'N/A'}</td>
                  <td className="td">
                    <Badge tone={statusTone[deployment.status] ?? 'slate'}>
                      {deployment.status.charAt(0).toUpperCase() + deployment.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="td text-theme-text-secondary">
                    {targetTypeLabels[deployment.target_type] || deployment.target_type}
                    {deployment.target_type === 'endpoint' && deployment.device_count != null && (
                      <span className="block text-[11px] text-theme-text-tertiary">
                        {deployment.device_count} device{deployment.device_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </td>
                  <td className="td">
                    {st === 'sap' && deployment.app_id ? (
                      <Link to={`/apps/${deployment.app_id}`} className="hover:opacity-80">
                        <Badge tone={badge.tone}>{badge.label}</Badge>
                      </Link>
                    ) : (
                      <Badge tone={badge.tone}>{badge.label}</Badge>
                    )}
                  </td>
                  <td className="td whitespace-nowrap text-theme-text-secondary">{dateTime(deployment.last_updated)}</td>
                  <td className="td text-right">
                    {/* Anything not already in the catalog can be packaged — an Intune app as
                        much as an unmanaged one; only the managed row has nothing left to do. */}
                    {st !== 'sap' ? (
                      <button
                        onClick={() => { setPackageOne(deployment); setShowModal(true) }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-theme-border-primary px-2.5 py-1.5 text-xs font-semibold text-theme-text-primary hover:bg-theme-tertiary"
                      >
                        <CubeIcon className="h-4 w-4" /> Package
                      </button>
                    ) : (
                      <span className="text-[12px] text-theme-text-tertiary">—</span>
                    )}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </TableCard>
      </>
      )}

      {/* Packaging: match the discovered app to a source, then submit the packaging job. */}
      <AddToCatalogModal
        isOpen={showModal}
        onClose={handleModalClose}
        apps={appsToPackage}
      />

      {SHOW_CONVERT_TO_MANAGED && (
        <ConvertToManagedModal
          isOpen={showConvertModal}
          onClose={handleConvertModalClose}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['conversions'] })}
          apps={selectedUnmanagedForConvert}
        />
      )}
    </div>
  )
}

const convTone: Record<DiscoveryPackagingJob['status'], BadgeTone> = {
  packaged: 'green',
  packaging: 'blue',
  queued: 'amber',
  failed: 'red',
  cancelled: 'slate',
  // An app that was already a certified public package: the library links to it, nothing was built.
  linked: 'green',
}

/** What Discovery did with the app, said in the row rather than left to the status word. */
const convOutcome: Record<NonNullable<DiscoveryPackagingJob['kind']>, string> = {
  package: 'Packaged',
  shortcut: 'Linked to the certified package',
}

/**
 * Conversions — every discovered app this page acted on, and what it became: a package built for
 * it, or a library entry linked to the certified public package it already had. Nothing here
 * uninstalls or deploys anything.
 */
function ConversionsView() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['discovery-packaging-jobs'],
    queryFn: listDiscoveryPackagingJobs,
    refetchInterval: 15000,
  })
  const rows = data?.jobs || []

  // Same as the list above: keep the rows we have when a refresh fails.
  if (error && !data) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        Failed to load conversions: {(error as Error).message}
      </p>
    )
  }

  if (!isLoading && rows.length === 0) {
    return (
      <div className="rounded-xl border border-theme-border-primary bg-theme-card">
        <EmptyState
          icon={<Squares2X2Icon className="h-7 w-7" />}
          title="No conversions yet"
          hint="When you add a discovered app to your library — packaged for you, or linked to the certified package it already has — it shows up here."
        />
      </div>
    )
  }

  return (
    <div>
      <p className="mb-4 text-sm text-theme-text-secondary">
        Apps you&apos;ve converted from discovery into managed, deployment-ready packages.
      </p>
      <TableCard
        footer={
          <span>
            {rows.length} conversion{rows.length !== 1 ? 's' : ''}
            {data?.truncated && ` — the ${data.limit ?? rows.length} most recent, older ones not shown`}
          </span>
        }
      >
        <thead>
          <tr className="border-b border-theme-border-primary">
            <th className="th">Source (discovered)</th>
            <th className="th"></th>
            <th className="th">Target (managed package)</th>
            <th className="th">Status</th>
            <th className="th">Job</th>
            <th className="th">When</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <TableSkeleton cols={6} />
          ) : (
            rows.map((c) => (
              <tr key={c.job_id} className="row-hover border-b border-theme-border-primary last:border-0">
                <td className="td">
                  <div className="max-w-[20rem]">
                    <div className="truncate font-medium text-theme-text-primary" title={c.source_display_name || undefined}>
                      {c.source_display_name || 'Unknown app'}
                    </div>
                    <div className="truncate text-[11px] text-theme-text-tertiary" title={c.source_publisher || undefined}>
                      {c.source_publisher || 'Unknown Publisher'}
                      {c.source_version ? ` · ${c.source_version}` : ''}
                    </div>
                  </div>
                </td>
                <td className="td text-theme-text-tertiary"><ArrowLongRightIcon className="h-4 w-4" /></td>
                <td className="td">
                  {/* Until the job finishes there is no package to link to, so the target is named
                      by what is being packaged. */}
                  {c.target_app_id ? (
                    <Link to={`/apps/${c.target_app_id}`} className="font-medium text-theme-accent-blue hover:underline">
                      {c.target_app_name || c.target_ref}
                    </Link>
                  ) : (
                    <span className="font-medium text-theme-text-primary">{c.target_app_name || c.target_ref || '—'}</span>
                  )}
                  {c.kind && (
                    <div className="text-[11px] text-theme-text-tertiary">{convOutcome[c.kind]}</div>
                  )}
                </td>
                <td className="td">
                  <Badge tone={convTone[c.status] ?? 'slate'}>{c.status}</Badge>
                  {c.status === 'failed' && c.error_message && (
                    <span className="mt-0.5 block max-w-[18rem] truncate text-[11px] text-theme-text-tertiary" title={c.error_message}>
                      {c.error_message}
                    </span>
                  )}
                </td>
                <td className="td font-mono text-[12px] text-theme-text-secondary">{c.job_id.slice(0, 8)}…</td>
                <td className="td whitespace-nowrap text-theme-text-secondary">{relTime(c.created_at)}</td>
              </tr>
            ))
          )}
        </tbody>
      </TableCard>
    </div>
  )
}

/** Page-level status strip. The mock draws these as a tinted panel with a matching hairline
 *  (its discovery page.tsx:82) rather than a solid alert block. */
function Banner({ tone, icon: Icon, title, children }: {
  tone: 'green' | 'amber' | 'red' | 'blue'
  icon: IconComponent
  title: string
  children: ReactNode
}) {
  const tones = {
    green: { box: 'border-emerald-500/25 bg-emerald-500/10', icon: 'text-emerald-500', title: 'text-emerald-600 dark:text-emerald-300' },
    amber: { box: 'border-amber-500/25 bg-amber-500/10', icon: 'text-amber-500', title: 'text-amber-600 dark:text-amber-300' },
    red: { box: 'border-red-500/25 bg-red-500/10', icon: 'text-red-500', title: 'text-red-600 dark:text-red-300' },
    blue: { box: 'border-theme-accent-blue/25 bg-theme-accent-blue/10', icon: 'text-theme-accent-blue', title: 'text-theme-accent-blue' },
  }[tone]
  return (
    <div className={`mb-5 flex items-start gap-3 rounded-xl border px-4 py-3 ${tones.box}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tones.icon}`} />
      <div>
        <p className={`text-sm font-semibold ${tones.title}`}>{title}</p>
        <p className="text-sm text-theme-text-secondary">{children}</p>
      </div>
    </div>
  )
}
